import React from 'react';
import Template from 'material-ui/svg-icons/av/web';
import Map from 'material-ui/svg-icons/av/web-asset';
import { push } from 'react-router-redux';
import {
  defineMessages,
  FormattedMessage,
} from 'react-intl';
import { modifyTemplate } from '../../api';
import {
  merge,
  sortByProps,
} from '@its/util';
import {
  setVisibility,
} from '@its/components/lib/Container components/LoadingContainer/actions';
import {
  setSuccessSnackBar,
  setSnackBar,
} from '@its/components/lib/Container components/SnackBarContainer/actions';
import {
  setTemplates,
  setTemplateList,
  setSelectedTemplate,
  setTemplateMaps,
  setTemplateMapList,
  setSelectedTemplateMap,
} from '../../redux/template/actions';
import {
  RowNumber,
} from '../../i18n/intl';

const DeletionFieldError = (
  <FormattedMessage
    id="DELETE_FIELD_ERROR"
    description="Notify deletion error"
    defaultMessage="Unable to delete field. Ensure that no display are using it."
  />
);

export const sortByRowNum = sortByProps.bind(null, 'rowNum', 'fieldNum');

export const adjustStartPositions = (template) => {
  for (const displayTemplateMap of template.displayTemplateMaps) {
    displayTemplateMap.startPosition = 1;
  }
  template.displayTemplateMaps.sort(sortByRowNum);
  let currentRow = 0;
  for (let i = 0; i < template.displayTemplateMaps.length; i++) {
    let previousStartPosition = 1;
    let previousWidth = 0;
    if (currentRow != template.displayTemplateMaps[i].rowNum) {
      currentRow = template.displayTemplateMaps[i].rowNum;
    } else {
      let j = i - 1;
      while (j >= 0 && template.displayTemplateMaps[j].isMarkedForDeletion && currentRow == template.displayTemplateMaps[j].rowNum) {
        j--;
      }
      if (j >= 0 && currentRow == template.displayTemplateMaps[j].rowNum) {
        previousWidth = Number(template.displayTemplateMaps[j].width);
        previousStartPosition = Number(template.displayTemplateMaps[j].startPosition);
      }
    }
    if (i === 0 || template.displayTemplateMaps[i].isMarkedForDeletion) {
      continue;
    }
    template.displayTemplateMaps[i].startPosition = (previousWidth + previousStartPosition);
  }
};

export const setSpecialFeatures = (map, values) => {
  map.specialFeatures = 0;
  if (values.multiCharacter) {
    map.specialFeatures += 1;
  }
  if (values.sliding) {
    map.specialFeatures += 2;
  }
  if (values.intermittence) {
    map.specialFeatures += 4;
  }
  if (values.indicator) {
    map.specialFeatures += 8;
  }
  delete values.sliding;
  delete values.intermittence;
  delete values.indicator;
  delete values.multiCharacter;
};

const messages = defineMessages({
  FIELD_NUM: {
    id: 'FIELD_NUM',
    description: 'Show field num for specific map',
    defaultMessage: 'Field {fieldNum}',
  },
  ROW_NUM: {
    id: 'ROW_NUM',
    description: 'Show row num for specific map',
    defaultMessage: 'Row {rowNum}',
  },
  FIELD_DESCRIPTION: {
    id: 'FIELD_DESCRIPTION',
    description: 'Field description',
    defaultMessage: 'From position {start} for {length} characters',
  },
});

const formatSpecialFeatures = (map) => {
  let value = map.specialFeatures;
  if (value - 8 >= 0) {
    value -= 8;
    map.indicator = true;
  }
  if (value - 4 >= 0) {
    value -= 4;
    map.intermittence = true;
  }
  if (value - 2 >= 0) {
    value -= 2;
    map.sliding = true;
  }
  if (value - 1 >= 0) {
    value -= 1;
    map.multiCharacter = true;
  }
  return map;
};

const handleTemplate = (template, dispatch, props) => {
  dispatch(setSelectedTemplate(template));
  const maps = template.displayTemplateMaps.map(formatSpecialFeatures);
  dispatch(setTemplateMaps(maps));
  dispatch(setTemplateMapList(getTemplateMapListProps(dispatch, template, maps, props)));
};

const getListProps = (dispatch, templates, props) => {
  const result = {
    title: 'TEMPLATES',
    items: [],
  };
  for (let i = 0; i < templates.length; i++) {
    result.items.push({
      key: templates[i].id,
      icon: <Template />,
      primaryText: templates[i].name,
      secondaryText: templates[i].shortDescription,
      editable: false,
      deletable: false,
      onTouchTap: () => {
        handleTemplate(templates[i], dispatch, props);
        dispatch(push('/template'));
      },
    });
  }
  return result;
};

const getTemplateMapListProps = (dispatch, template, maps, props) => {
  const { formatMessage } = props.intl;
  const result = [];
  maps = maps.sort(sortByRowNum);
  let j = -1;
  let currentRow = -1;
  for (let i = 0; i < maps.length; i++) {
    if (maps[i].isMarkedForDeletion) {
      continue;
    }
    if (currentRow !== maps[i].rowNum) {
      currentRow = maps[i].rowNum;
      result[currentRow] = {
        title: RowNumber(maps[i].rowNum),
        items: [],
      };
    }
    result[currentRow].items.push({
      key: maps[i].id,
      icon: <Map />,
      primaryText: formatMessage(messages.FIELD_NUM, { fieldNum: maps[i].fieldNum }),
      secondaryText: formatMessage(messages.FIELD_DESCRIPTION, { start: maps[i].startPosition, length: maps[i].width }),
      editable: false,
      deletable: true,
      onDelete: () => {
        dispatch(setVisibility(true));
        const value = merge({}, template);
        value.displayTemplateMaps = [...maps];
        const index = value.displayTemplateMaps.indexOf(maps[i]);
        value.displayTemplateMaps[index].isMarkedForDeletion = true;
        adjustStartPositions(value);
        modifyTemplate(template.id)(value).then((data) => {
          onModificationSuccess(data, dispatch, props);
          dispatch(setSuccessSnackBar());
        }).catch(() => {
          dispatch(setSnackBar({
            message: DeletionFieldError,
            show: true,
          }));
        }).finally(() => {
          dispatch(setVisibility(false));
        });
      },
      onTouchTap: () => {
        dispatch(setSelectedTemplateMap(maps[i]));
        dispatch(push('template/map'));
      },
    });
  }
  return result;
};

export const onSuccess = (result, dispatch, props) => {
  dispatch(setTemplates(result.body.resultSet));
  dispatch(setTemplateList(getListProps(dispatch, result.body.resultSet, props)));
};

export const onModificationSuccess = (result, dispatch, props) => {
  handleTemplate(result.body, dispatch, props);
};



/** WEBPACK FOOTER **
 ** ./src/routes/Template/util.js
 **/